import {Anchor, Text} from '@mantine/core';
import React from 'react';

import {isSimpleClick} from 'BenchlingUtil';
import navigateToURL, {confirmNavigationWarnings} from 'lib/navigateToURL';

// A notification body is plain text that may embed links using a `<bnch_link>` tag whose contents
// are a JSON object `{"text": label, "url": path}`, as materialized by the server. This is the ONLY
// place that knows that convention — the row renderer consumes the parsed segments and is agnostic
// to the wire format.

interface TextSegment {
  type: 'text';
  text: string;
}

interface LinkSegment {
  type: 'link';
  text: string;
  // A same-origin relative path (e.g. `/chats/abc`), suitable for use as an anchor href.
  path: string;
}

export type BodySegment = TextSegment | LinkSegment;

// Matches a single link tag and captures its JSON payload. Global so we can walk every match; a
// body may embed zero or more links. The payload is JSON-encoded with `<` escaped as `\u003c`, so it
// cannot contain the closing delimiter.
const LINK_TAG = /<bnch_link>(.*?)<\/bnch_link>/g;

// A link URL must be a same-origin relative path: a leading `/` not followed by `/` or `\` (which
// browsers normalize into a protocol-relative, cross-origin URL), and no whitespace or backslashes.
// Mirrors the server-side `Link` validation (which also rejects control characters at write time)
// so a bad payload renders as plain text rather than a dangerous anchor.
const SAME_ORIGIN_PATH = /^\/(?![/\\])[^\s\\]*$/;

interface LinkPayload {
  text: string;
  url: string;
}

function parsePayload(raw: string): LinkPayload | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    // A malformed payload throws SyntaxError; treat it as unparseable. Anything else is unexpected
    // and should surface rather than be swallowed.
    if (e instanceof SyntaxError) {
      return null;
    }
    throw e;
  }
  if (
    typeof parsed !== 'object' ||
    parsed == null ||
    typeof (parsed as LinkPayload).text !== 'string' ||
    typeof (parsed as LinkPayload).url !== 'string'
  ) {
    return null;
  }
  return parsed as LinkPayload;
}

/**
 * Split a body string into text and link segments. A link whose payload is unparseable, or whose
 * URL is not a same-origin relative path, renders as plain text (its label, or the raw tag when the
 * payload can't be read) rather than a clickable anchor, so a malicious or malformed payload can
 * never produce a dangerous link. An empty URL is a valid "unlinked text" signal from the server
 * and likewise renders the label as plain text.
 */
export function parseBody(body: string): Array<BodySegment> {
  const segments: Array<BodySegment> = [];
  let lastIndex = 0;
  for (const match of body.matchAll(LINK_TAG)) {
    const [full, rawPayload] = match;
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      segments.push({type: 'text', text: body.slice(lastIndex, matchIndex)});
    }
    const payload = parsePayload(rawPayload);
    if (payload == null) {
      // Unparseable payload — keep the raw tag text so nothing is silently dropped.
      segments.push({type: 'text', text: full});
    } else if (payload.url !== '' && SAME_ORIGIN_PATH.test(payload.url)) {
      segments.push({type: 'link', text: payload.text, path: payload.url});
    } else {
      // Empty or unsafe URL — fall back to the label text.
      segments.push({type: 'text', text: payload.text});
    }
    lastIndex = matchIndex + full.length;
  }
  if (lastIndex < body.length) {
    segments.push({type: 'text', text: body.slice(lastIndex)});
  }
  return segments;
}

interface NotificationBodyProps {
  body: string;
  // Called when a link in the body is activated. `openedInPlace` is true for a simple click, which
  // navigates in the current tab, and false for a modified click (e.g. Cmd/Ctrl-click) left to the
  // browser to open elsewhere. The bell uses this to mark the notification read and, only when
  // opened in place, close the panel.
  onLinkClick: (openedInPlace: boolean) => void;
}

/**
 * Render a notification body, turning any embedded link into a clickable anchor. A simple click
 * navigates to its path in place; a modified click keeps the browser's native behavior. Either way,
 * {@link NotificationBodyProps.onLinkClick} is called, unless the user cancels an unsaved-changes
 * prompt for an in-place navigation.
 */
export default function NotificationBody({body, onLinkClick}: NotificationBodyProps): JSX.Element {
  const segments = parseBody(body);
  return (
    <Text span={true} inherit={true}>
      {segments.map((segment, index) =>
        segment.type === 'link' ? (
          <Anchor
            key={index}
            href={segment.path}
            onClick={(e) => {
              if (!isSimpleClick(e)) {
                // Let the browser open the href natively (e.g. in a new tab).
                onLinkClick(false);
                return;
              }
              e.preventDefault();
              // Confirm any unsaved-changes warning before reporting the click, so a cancelled
              // navigation leaves the notification unread and the panel open. Clearing on confirm
              // keeps navigateToURL from prompting a second time.
              if (!confirmNavigationWarnings({clearOnConfirm: true})) {
                return;
              }
              // Report the click before navigating so the mark-read request is dispatched ahead of
              // the destination route's synchronous render and its own requests.
              onLinkClick(true);
              // Navigate in place through the client router rather than a full-page load, so the
              // matched route runs (e.g. a chat session opens in the AI sidepanel).
              navigateToURL(segment.path);
            }}
            weight={600}
            inherit={true}
          >
            {segment.text}
          </Anchor>
        ) : (
          <React.Fragment key={index}>{segment.text}</React.Fragment>
        )
      )}
    </Text>
  );
}
